import { BillingAmountUpdateResultDto } from './dto/billing-amount-update-result.dto';
import { NotificationService } from '../../shared/notification/notification.service';
import { UtilService } from '../../shared/util/util.service';
import { BillingAmountUpdatePreviewDto } from './dto/billing-amount-update-preview.dto';
import { BillingService } from '../shared/billing/billing.service';
import { BillingAmountUpdateDto } from './dto/billing-amount-update.dto';
import { PagedResult } from '../../shared/model/generic-paged-result.model';
import { ArcSearch } from '../shared/arc/arc-search.model';
import { SelectItem, MenuItem } from 'primeng/api';
import { TranslationService } from '../../shared/translation/translation.service';
import { Component, OnInit, OnChanges, Input, Output, SimpleChanges, EventEmitter } from '@angular/core';
import { BillingAmountUpdateMethod } from './dto/billing-amount-update-method.enum';
import { ArcService } from '../shared/arc/arc.service';
import { ArcFormula } from '../shared/arc';
import { DatagridService, PagedResultGeneric } from 'src/app/shared';
import { LazySelectableTableComponent } from 'src/app/shared/primeng/abstract-classes/lazy-selectable-table.component';
import { FinancialServiceService } from '../shared/financial.service';
import { NavigationService } from 'src/app/shared/navigation/navigation.service';

@Component({
  selector: 'wl-billing-amount-update',
  templateUrl: './billing-amount-update.component.html',
  styleUrls: ['./billing-amount-update.component.less'],
    standalone: false
})
export class BillingAmountUpdateComponent
  extends LazySelectableTableComponent<BillingAmountUpdateDto, BillingAmountUpdatePreviewDto>
  implements OnInit, OnChanges
{
  @Input() anchorMonth: number;
  @Input() revenueItems = [];
  @Input() billingYear: number;
  @Output() cancelUpdate = new EventEmitter();

  public updateMethods: SelectItem[];
  public pricingCalcs: ArcFormula[];
  public previewActionOptions: MenuItem[];
  public isSaving = false;
  public saveCompleted = false;

  // custom getter/setter for chosen billing update calc method
  get selectedUpdateMethod() {
    return this.searchParams.UpdateAmountMethod;
  }
  set selectedUpdateMethod(method: BillingAmountUpdateMethod) {
    this.searchParams.UpdateAmountMethod = +method;
  }

  // custom getter/setter for chosen pricing calculation
  get selectedPricingCalc() {
    return this.searchParams.PricingCalcID;
  }
  set selectedPricingCalc(id: number) {
    this.searchParams.PricingCalcID = id;
  }

  constructor(
    private _translateService: TranslationService,
    private _arcService: ArcService,
    private _billingService: BillingService,
    private _utilService: UtilService,
    private _notificationService: NotificationService,
    private _financialService: FinancialServiceService,
    private _navigationService: NavigationService,
    _datagridService: DatagridService
  ) {
    super('BillingId', _datagridService);
  }

  async ngOnInit() {
    this.searchParams = new BillingAmountUpdateDto();
    this.updateMethods = new Array<SelectItem>();

    this.updateMethods = [
      {
        label: await this._translateService.get('revenue.billing-amount-update.fixed-amount'),
        value: BillingAmountUpdateMethod.FixedAmount,
      },
      {
        label: await this._translateService.get('revenue.billing-amount-update.percentage'),
        value: BillingAmountUpdateMethod.Percentage,
      },
      {
        label: await this._translateService.get('revenue.billing-amount-update.pricing-calc'),
        value: BillingAmountUpdateMethod.PricingCalculation,
      },
      {
        label: await this._translateService.get('revenue.billing-amount-update.membership-level'),
        value: BillingAmountUpdateMethod.MembershipLevel,
      },
    ];

    this._arcService.searchArcs({ PageSize: 0 } as ArcSearch).then((data: PagedResult<Array<ArcFormula>>) => {
      this.pricingCalcs = data.Result;
    });

    this.initActionsOptions();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.anchorMonth && this.searchParams) {
      this.searchParams.AnchorMonth = this.anchorMonth;
    }

    if (changes.revenueItems && this.searchParams) {
      this.searchParams.RevenueItemNums = this.revenueItems.map((x) => x.ItemNum);
    }

    if (changes.billingYear && this.searchParams) {
      this.searchParams.BillingYear = this.billingYear;
    }
  }

  fetchResults(): Promise<PagedResult<BillingAmountUpdatePreviewDto[]>> {
    return this._billingService.previewAmountUpdate(this.searchParams);
  }

  previewClick() {
    this.searchParams.PageSize = 20;
    this.search();
  }

  cancel() {
    this.searchResult = null;
    this.searchParams = new BillingAmountUpdateDto();
    this.searchParams.AnchorMonth = this.anchorMonth;
    this.searchParams.RevenueItemNums = this.revenueItems.map((x) => x.ItemNum);
    this.searchParams.BillingYear = this.billingYear;
    this.reset();
    this.saveCompleted = false;
    this.cancelUpdate.emit();
  }

  removeSelected() {
    this.fetchAllSelectedIds().then((excluded: number[]) => {
      this.searchParams.ExcludedBillingIds = excluded;
      this.search();
    });
  }

  ExportProposedChanges() {
    this._financialService.exportProposedBillingChanges(this.searchParams).then((csvUrl) => {
      this._navigationService.popupSafeNavigate(csvUrl);
    });
  }
  async persistUpdates() {
    this.isSaving = true;

    try {
      let fullList = await this.getAllPreviewAmmounts();

      let updateSuccesses = 0;
      for (let billing of fullList?.Result) {
        const updateDto = new PagedResult<BillingAmountUpdatePreviewDto[]>();
        updateDto.Result = [billing];
        const result = await this._billingService.persistBillingAmountUpdates(updateDto);
        if (result.UpdatedCount > 0) {
          updateSuccesses++;
        }
        if (result.HasErrors) {
          result.Messages.forEach((x) => {
            this._notificationService.error(x);
          });
        }
      }

      if (updateSuccesses > 0) {
        const successMessage = this._translateService.getInstant('revenue.billing-amount-update.successfully-updated');
        this._notificationService.persistentSuccess(`${updateSuccesses} ${successMessage}`);
        this.saveCompleted = true;
      }
    } catch (err) {
      this._notificationService.httpError(err);
    } finally {
      this.isSaving = false;
    }
  }

  async getAllPreviewAmmounts(): Promise<PagedResultGeneric<BillingAmountUpdatePreviewDto>> {
    const params = this._utilService.clone(this.searchParams);
    params.PageSize = 300;
    params.PageNumber = 1;
    // get the results once outside the loop, then we loop through a bunch and append to the existing set.
    let results = await this._billingService.previewAmountUpdate(params);
    while (params.PageNumber < results.TotalPages) {
      params.PageNumber++;
      let moarResults = await this._billingService.previewAmountUpdate(params);
      results.Result.push(...moarResults.Result);
    }
    console.info(results.Result.length);
    results.PageSize = 0;
    return results;
  }

  private async initActionsOptions() {
    this.previewActionOptions = [
      {
        label: await this._translateService.get('revenue.billing-amount-update.actions.remove-selected'),
        command: (event) => {
          this.removeSelected();
        },
      },
      {
        label: await this._translateService.getInstantTitlecase('revenue.billing-amount-update.actions.export-all-proposed'),
        command: (event) => {
          this.ExportProposedChanges();
        },
      },
    ];
  }
}
